import { Icon } from '@ur-solutions/react-components'
import { Input } from '@/components_deprecated/Input'
import groupBy from 'lodash/groupBy'
import type { ReactNode} from 'react';
import React, { useMemo, useState } from 'react'
import styled from 'styled-components'
import { ZIndexRange } from '@/types/style'

const Wrapper = styled.table`
  width: 100%;
  border-collapse: collapse;

  tbody tr:nth-child(odd) {
    background-color: ${props => props.theme.colors.quaternary};
  }
  tbody td {
    padding: 6px 6px 3px;

    &:first-of-type {
      border-top-left-radius: 4px;
      border-bottom-left-radius: 4px;
    }
    &:last-of-type {
      border-top-right-radius: 4px;
      border-bottom-right-radius: 4px;
    }
  }
  thead th {
    position: sticky;
    top: 0;
    background-color: ${props => props.theme.colors.white};
    z-index: ${ZIndexRange.FormFields};
  }
`
const HeaderTitle = styled.td`
  font-weight: 600;
  font-size: 0.9rem;
  text-transform: uppercase;
`
const Strike = styled.td.attrs({
  colSpan: 9999,
})`
  position: relative;
  z-index: 0;
  &::after {
    content: '';
    position: absolute;
    width: calc(100% - 2rem);
    height: 2px;
    left: 1rem;
    top: calc(50% - 1px);
    background-color: ${props => props.theme.colors.primary}22;
  }
`
const Row = styled.tr`
  &:hover td {
    color: ${props => props.theme.colors.secondary} !important;
  }
`
const RowTitle = styled.td`
  color: ${props => props.theme.colors.gray4};
`
const CheckCell = styled.td`
  & > div {
    display: flex;
    justify-content: center;
  }
`

const SlimInput = styled(Input)``

export interface CheckedTableValue {
  column: string
  row: string
  /**
   * Determines wheter the cell should be checked or not, if it can't be toggled
   * it is null.
   */
  checked: boolean | null
}

export interface CheckedTableRow {
  type: 'header' | 'regular'
  title: ReactNode
  value: string
  noStrike?: boolean
}

export interface CheckedTableCell {
  value: string
}

interface CheckedTableProps {
  value: CheckedTableValue[]

  columns: ReactNode[]
  rows: CheckedTableRow[]
  rowFilter?: string
  searchable?: boolean

  onChange: (collection: CheckedTableValue[]) => void
}

export const CheckedTable: React.FC<CheckedTableProps> = ({
  value,

  columns,
  rows,
  searchable = false,

  onChange,
}) => {
  const [rowFilter, setRowFilter] = useState<string>('')
  const valuesByRow = useMemo(() => groupBy(value, 'row'), [value])

  function handleToggle(current: CheckedTableValue) {
    const valueIdx = value.findIndex(
      val => val.row === current.row && val.column === current.column
    )
    if (valueIdx < 0) return

    const newValue = [...value]
    const newValueAtIdx = newValue[valueIdx]
    if (!newValueAtIdx) return

    newValue.splice(valueIdx, 1, {
      ...newValueAtIdx,
      checked: !current.checked,
    })

    onChange(newValue)
  }

  const filteredRows = [] as CheckedTableRow[]
  let includeRows = true
  rows.forEach(row => {
    if (
      row.type === 'header' &&
      row.value.toLowerCase().includes(rowFilter.toLowerCase())
    ) {
      includeRows = true
    } else if (row.type === 'header') {
      includeRows = false
    }
    if (includeRows) {
      filteredRows.push(row)
    }
  })

  return (
    <Wrapper>
      <thead>
        <tr>
          <th>
            {searchable && (
              <SlimInput
                value={rowFilter}
                onChange={value => setRowFilter(value)}
                iconLeftProps={{ icon: 'search', type: 'light' }}
                height="2rem"
                borderRadius="4px"
                fontSize="1rem"
                fullWidth
              />
            )}
          </th>
          {columns.map((column, idx) => (
            <th key={idx}>{column}</th>
          ))}
        </tr>
      </thead>

      <tbody>
        {filteredRows.map(row => {
          const values = valuesByRow[row.value] ?? []

          return row.type === 'header' ? (
            <tr key={row.value}>
              <HeaderTitle>{row.title}</HeaderTitle>
              {!row.noStrike ? <Strike /> : <td colSpan={9999} />}
            </tr>
          ) : (
            <Row key={row.value}>
              <RowTitle>{row.title}</RowTitle>

              {values.map(value => (
                <CheckCell key={value.column + value.row}>
                  {value.checked !== null ? (
                    <div>
                      <Icon
                        icon={value.checked ? 'check' : 'times'}
                        type="light"
                        color={value.checked ? 'success' : 'red'}
                        cursor="pointer"
                        onClick={() => handleToggle(value)}
                      />
                    </div>
                  ) : (
                    <div></div>
                  )}
                </CheckCell>
              ))}
            </Row>
          )
        })}
      </tbody>
    </Wrapper>
  )
}
